import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Briefcase,
  Building2,
  Users,
  Globe,
  Settings as GlobalSettingsIcon,
} from 'lucide-react';

interface AdminNavRailProps {
  onSettingsClick: () => void;
}

/**
 * Purpose: Provides primary navigation for platform admins within the /admin path.
 * Location: src/components/layout/AdminNavRail.tsx
 * Constraints: Fixed width, vertical layout, uses Lucide icons. Links are relative to /admin.
 */
export function AdminNavRail({ onSettingsClick }: AdminNavRailProps) {
  const location = useLocation();

  const navItems = [
    {
      name: 'Dashboard',
      href: '/admin',
      icon: LayoutDashboard,
      current:
        location.pathname === '/admin' ||
        location.pathname === '/admin/dashboard',
    },
    {
      name: 'Communities',
      href: '/admin/communities',
      icon: Globe,
      current: location.pathname.startsWith('/admin/communities'),
    },
    {
      name: 'Jobs',
      href: '/admin/jobs',
      icon: Briefcase,
      current: location.pathname.startsWith('/admin/jobs'),
    },
    {
      name: 'Employers',
      href: '/admin/employers',
      icon: Building2,
      current: location.pathname.startsWith('/admin/employers'),
    },
    {
      name: 'Users',
      href: '/admin/users',
      icon: Users,
      current: location.pathname.startsWith('/admin/users'),
    },
  ];

  return (
    <div className="fixed top-16 left-0 h-[calc(100vh-4rem)] w-20 bg-white shadow-md flex flex-col items-center py-4 space-y-3 z-40">
      {navItems.map((item) => (
        <Link
          key={item.name}
          to={item.href}
          className={`flex flex-col items-center justify-center p-3 rounded-lg transition-colors w-16 h-16
            ${item.current ? 'bg-gray-100 text-gray-700' : 'text-gray-500 hover:bg-gray-100 hover:text-gray-700'}`}
          title={item.name}
        >
          <item.icon
            className={`h-6 w-6 ${item.current ? 'text-gray-700' : 'text-gray-500'}`}
          />
          <span className="mt-1 text-xs font-medium">{item.name}</span>
        </Link>
      ))}

      <div className="mt-auto flex flex-col items-center space-y-3 w-full">
        <button
          onClick={onSettingsClick}
          className="flex flex-col items-center justify-center p-3 rounded-lg text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors w-16 h-16"
          title="Platform Settings"
        >
          <GlobalSettingsIcon className="h-6 w-6 text-gray-500" />
          <span className="mt-1 text-xs font-medium">Settings</span>
        </button>
      </div>
    </div>
  );
}
